/**
 * Patterns that repeat across the approved screens.
 *
 * Every one of these exists because the same shape appears on three or more
 * mockups: the page header with a trailing action, the pipeline stepper, the
 * right-hand inspector, the labelled metric row, the summary tile. Building
 * them once is what stops each screen becoming a seven-hundred-line component
 * with its own slightly different spacing.
 *
 * None of them hold data or fetch anything. They are given values or told a
 * value is absent, and "absent" is a first-class case throughout: a metric
 * with no value renders a dash, never a zero, because a zero is a measurement.
 */

import type { ReactNode } from "react";
import { IconChevronRight } from "./icons";
import "./product.css";

export type Tone = "success" | "caution" | "danger" | "neutral" | "accent";

/* --- page header ---------------------------------------------------------- */

export function PageHeader({
  title,
  subtitle,
  meta,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div className="page-header__text">
        <h1>{title}</h1>
        {subtitle && <p className="page-header__subtitle">{subtitle}</p>}
      </div>
      {meta && <div className="page-header__meta">{meta}</div>}
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}

/* --- metrics -------------------------------------------------------------- */

/**
 * One labelled value. `value` of `null`/`undefined` renders an em dash - the
 * distinction between "we measured nothing" and "we measured zero" is the
 * whole reason this component exists rather than a template string.
 */
export function Metric({
  label,
  value,
  tone,
  help,
}: {
  label: string;
  value: ReactNode;
  tone?: Tone;
  help?: string;
}) {
  const absent = value === null || value === undefined || value === "";
  return (
    <div className="metric">
      <span className="metric__label">
        {label}
        {help && <span className="metric__help">{help}</span>}
      </span>
      <span className={`metric__value${tone && !absent ? ` metric__value--${tone}` : ""}`}>
        {absent ? "—" : value}
      </span>
    </div>
  );
}

/* --- inspector ------------------------------------------------------------ */

export function InspectorPanel({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="inspector">
      <header className="inspector__head">
        <h2>{title}</h2>
        {badge}
      </header>
      <div className="inspector__body">{children}</div>
    </section>
  );
}

/* --- pipeline stepper ----------------------------------------------------- */

export interface Stage {
  n: string;
  label: string;
  /** The state word shown under the label. Always a real state, never a guess. */
  state: string;
  tone: Tone;
}

/**
 * The reconstruction pipeline across the top of the workstation screen.
 *
 * Rendered as an ordered list so a screen reader reads it as the sequence it
 * is, and each stage carries its state as text rather than only as a colour.
 */
export function StageStepper({ stages }: { stages: Stage[] }) {
  return (
    <ol className="stepper">
      {stages.map((stage) => (
        <li key={stage.n} className="stepper__item">
          <span className="stepper__n">{stage.n}</span>
          <span className="stepper__label">{stage.label}</span>
          <span className={`stepper__state stepper__state--${stage.tone}`}>{stage.state}</span>
        </li>
      ))}
    </ol>
  );
}

/* --- summary tile --------------------------------------------------------- */

/**
 * The four-across maturity strip on the project overview, and the stacked
 * cards beside it. `to` makes the whole tile a link; without it the tile is
 * inert rather than looking clickable and doing nothing.
 */
export function SummaryTile({
  icon,
  title,
  state,
  tone = "neutral",
  detail,
  badge,
  onOpen,
}: {
  icon?: ReactNode;
  title: string;
  state: ReactNode;
  tone?: Tone;
  detail?: ReactNode;
  badge?: ReactNode;
  onOpen?: () => void;
}) {
  const body = (
    <>
      <div className="tile__head">
        <h3>{title}</h3>
        {badge}
      </div>
      <div className="tile__body">
        {icon && <span className={`tile__icon tile__icon--${tone}`}>{icon}</span>}
        <div>
          <p className={`tile__state tile__state--${tone}`}>{state}</p>
          {detail && <p className="tile__detail">{detail}</p>}
        </div>
      </div>
      {onOpen && <IconChevronRight size={18} className="tile__chevron" />}
    </>
  );

  if (!onOpen) return <section className="tile">{body}</section>;
  return (
    <button type="button" className="tile tile--action" onClick={onOpen}>
      {body}
    </button>
  );
}

/* --- notice --------------------------------------------------------------- */

/**
 * The banded "recommended next step" strip. Tone is carried by an icon and a
 * word as well as the band colour.
 */
export function Notice({
  tone = "neutral",
  eyebrow,
  title,
  children,
  action,
}: {
  tone?: Tone;
  eyebrow?: string;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className={`notice notice--${tone}`}>
      <div className="notice__text">
        {eyebrow && <p className="notice__eyebrow">{eyebrow}</p>}
        <p className="notice__title">{title}</p>
        {children && <div className="notice__body">{children}</div>}
      </div>
      {action && <div className="notice__action">{action}</div>}
    </section>
  );
}

/* --- section -------------------------------------------------------------- */

export function Section({
  title,
  description,
  actions,
  children,
}: {
  title?: string;
  /** One muted line under the heading. Says what the section is for. */
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="panel">
      {(title || actions) && (
        <header className="panel__head">
          {title && <h2>{title}</h2>}
          {actions}
        </header>
      )}
      {description && <p className="panel__description">{description}</p>}
      {children}
    </section>
  );
}
